/**
 * TOSS Web Application - Protected Route Component
 *
 * Route wrapper that requires authentication.
 * Redirects unauthenticated users to the login page.
 *
 * @example
 * ```tsx
 * <Route
 *   path="/profile"
 *   element={
 *     <ProtectedRoute>
 *       <ProfilePage />
 *     </ProtectedRoute>
 *   }
 * />
 * ```
 */

import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useIsAuthenticated, useAuthStore, selectIsInitializing, selectIsAuthPermissionsLoaded } from '@/store';
import { ROUTES } from '@/constants';
import { PageLoading } from '@/components/ui';

// ============================================================================
// Types
// ============================================================================

/**
 * Props for ProtectedRoute component
 */
export interface ProtectedRouteProps {
  /** Child elements to render if authenticated */
  children: ReactNode;
  /**
   * Path to redirect to if not authenticated
   * @default ROUTES.auth.login
   */
  redirectTo?: string;
  /**
   * Whether to preserve the current location in state for redirect back
   * @default true
   */
  preserveLocation?: boolean;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Protected route wrapper component
 *
 * Checks if the user is authenticated and renders children if so.
 * Otherwise, redirects to the login page.
 *
 * During auth initialization (checking stored tokens), shows a loading spinner.
 *
 * The original location is preserved in the redirect state, so after login,
 * the user can be redirected back to the originally requested page.
 *
 * @param props - Component props
 * @returns Protected route element
 *
 * @example
 * ```tsx
 * // Basic usage
 * <ProtectedRoute>
 *   <ProfilePage />
 * </ProtectedRoute>
 *
 * // Custom redirect
 * <ProtectedRoute redirectTo="/custom-login">
 *   <SecretPage />
 * </ProtectedRoute>
 *
 * // Without location preservation
 * <ProtectedRoute preserveLocation={false}>
 *   <SomePage />
 * </ProtectedRoute>
 * ```
 */
export function ProtectedRoute({
  children,
  redirectTo = ROUTES.auth.login,
  preserveLocation = true,
}: ProtectedRouteProps): JSX.Element {
  const isAuthenticated = useIsAuthenticated();
  const isInitializing = useAuthStore(selectIsInitializing);
  const isPermissionsLoaded = useAuthStore(selectIsAuthPermissionsLoaded);
  const location = useLocation();

  // Show loading during auth initialization
  if (isInitializing) {
    return <PageLoading />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Preserve the current location so we can redirect back after login
    const state = preserveLocation ? { from: location } : undefined;
    return <Navigate to={redirectTo} state={state} replace />;
  }

  // Block children until dynamic permissions have resolved — prevents a flash
  // of UI elements the user may not have access to right after login.
  if (!isPermissionsLoaded) {
    return <PageLoading />;
  }

  // User is authenticated and permissions are ready, render children
  return <>{children}</>;
}

// ============================================================================
// Hook for accessing redirect location
// ============================================================================

/**
 * Location state with redirect-from information
 */
export interface LocationState {
  from?: { pathname: string; search?: string; hash?: string };
}

/**
 * Get the redirect-from location from router state
 *
 * Use this in the login page to redirect users back to where they came from.
 *
 * @param defaultPath - Default path if no redirect location is stored
 * @returns The path to redirect to after login
 *
 * @example
 * ```tsx
 * function LoginPage() {
 *   const location = useLocation();
 *   const navigate = useNavigate();
 *   const redirectPath = getRedirectPath(location, '/dashboard');
 *
 *   const handleLoginSuccess = () => {
 *     navigate(redirectPath, { replace: true });
 *   };
 *
 *   return <LoginForm onSuccess={handleLoginSuccess} />;
 * }
 * ```
 */

// ============================================================================
// Default Export
// ============================================================================

export default ProtectedRoute;
